import { useEffect } from 'react';

/**
 * Lightweight SEO component (no external deps).
 * Sets document.title, meta description, OG tags, and optional JSON-LD.
 * Cleans up page-specific JSON-LD on unmount.
 */
const SEO = ({
  title,
  description,
  keywords,
  path = '/',
  type = 'website',
  image = 'https://images.unsplash.com/photo-1769629918261-92d1fac176cd',
  jsonLd,
}) => {
  useEffect(() => {
    const baseUrl = window.location.origin;
    const fullUrl = `${baseUrl}${path}`;

    if (title) document.title = title;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => {
          if (k !== 'content') el.setAttribute(k, v);
        });
        document.head.appendChild(el);
      }
      el.setAttribute('content', attrs.content || '');
      return el;
    };

    const setLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
      return el;
    };

    if (description) {
      setMeta('meta[name="description"]', { name: 'description', content: description });
      setMeta('meta[property="og:description"]', { property: 'og:description', content: description });
      setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    }
    if (keywords) {
      setMeta('meta[name="keywords"]', { name: 'keywords', content: keywords });
    }
    if (title) {
      setMeta('meta[property="og:title"]', { property: 'og:title', content: title });
      setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    }
    setMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: fullUrl });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: image });
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });

    setLink('canonical', fullUrl);

    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement('script');
      jsonLdEl.type = 'application/ld+json';
      jsonLdEl.text = JSON.stringify(jsonLd);
      jsonLdEl.setAttribute('data-page-jsonld', 'true');
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      if (jsonLdEl && jsonLdEl.parentNode) {
        jsonLdEl.parentNode.removeChild(jsonLdEl);
      }
    };
  }, [title, description, keywords, path, type, image, jsonLd]);

  return null;
};

export default SEO;
